/**
 * F.6.4 · Card primaria "DOCUMENTI E FIRMA CONTRATTO".
 * Gestisce il lifecycle documentale del Subcontract dalla pagina dettaglio:
 *   BOZZA            -> Genera PDF contratto / Scarica / Segna pronto per firma
 *   PRONTO PER FIRMA -> Scarica PDF / Carica firmato / Conferma firma
 *   FIRMATO          -> Documento firmato attivo (Visualizza / Scarica)
 * Backend autoritativo: i guardrail sono verificati anche lato API.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  FileText, Download, Upload, CheckCircle2, Send, Eye,
  Loader2, AlertTriangle, FileSignature,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem('token');
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const _forceDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'documento.pdf'; a.style.display = 'none';
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
};

const _openBlob = (blob) => {
  const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const _filenameFromResponse = (r, fallback) => {
  const cd = r.headers?.['content-disposition'] || r.headers?.['Content-Disposition'] || '';
  const xf = r.headers?.['x-filename'] || r.headers?.['X-Filename'];
  if (xf) return xf;
  const m = /filename="?([^"]+)"?/i.exec(cd);
  return m ? m[1] : fallback;
};

const SubcontractDocumentsCard = ({ subcontract: sub, onChanged, usersMap = {} }) => {
  const [signed, setSigned] = useState({ active: null, history: [] });
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [confirmSign, setConfirmSign] = useState(false);
  const fileRef = useRef(null);

  const status = sub?.status;

  const loadSigned = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/subcontracts/${sub.id}/signed-pdf/active`, auth());
      setSigned({ active: r.data?.active || null, history: r.data?.history || [] });
    } catch {
      setSigned({ active: null, history: [] });
    }
  }, [sub.id]);

  useEffect(() => { loadSigned(); }, [loadSigned]);

  const operatorName = (uid) => usersMap[uid] || uid || '—';

  // Documento generato è effimero (download-only): "Genera" e "Scarica" chiamano lo stesso endpoint.
  const generatePdf = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/api/subcontracts/${sub.id}/generate-pdf`, {}, {
        ...auth(), responseType: 'blob',
      });
      const fn = _filenameFromResponse(r, `Contratto_Subappalto_${sub.subcontract_number}.pdf`);
      _forceDownload(r.data, fn);
      toast.success('PDF contratto generato');
      onChanged && onChanged();
    } catch (e) {
      let msg = 'Errore generazione PDF';
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text())?.detail || msg; } catch { /* noop */ }
      } else { msg = e.response?.data?.detail || msg; }
      toast.error(msg);
    } finally { setGenerating(false); }
  };

  const uploadSigned = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/api/subcontracts/${sub.id}/upload-signed-pdf`, fd, auth());
      toast.success(`Contratto firmato caricato · v${r.data?.version || '?'}`);
      await loadSigned();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload contratto firmato');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const downloadDoc = async (docId, fallback, view = false) => {
    try {
      const r = await axios.get(`${API}/api/subcontracts/${sub.id}/documents/${docId}/download`, {
        ...auth(), responseType: 'blob',
      });
      if (view) _openBlob(r.data);
      else _forceDownload(r.data, _filenameFromResponse(r, fallback));
    } catch {
      toast.error('Errore apertura documento');
    }
  };

  const transition = async (target) => {
    setTransitioning(true);
    try {
      await axios.post(`${API}/api/subcontracts/${sub.id}/transition`, { target_status: target }, auth());
      toast.success(target === 'firmato' ? 'Contratto firmato' : 'Stato aggiornato');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transizione non consentita');
    } finally {
      setTransitioning(false);
      setConfirmSign(false);
    }
  };

  const pdfGenerated = !!sub.pdf_generated_at;
  const hasSigned = !!signed.active;

  let docState = 'Da generare', docBadge = 'bg-slate-100 text-slate-700 border-slate-300';
  if (status === 'firmato' || hasSigned) { docState = 'Firmato'; docBadge = 'bg-emerald-100 text-emerald-700 border-emerald-300'; }
  else if (pdfGenerated) { docState = 'Generato'; docBadge = 'bg-blue-100 text-blue-700 border-blue-300'; }

  return (
    <Card className="border-teal-200 shadow-sm" data-testid="sd-docs-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileSignature className="w-5 h-5 text-teal-600" />
            Documenti e Firma Contratto
          </CardTitle>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">Stato documento:</span>
            <Badge className={docBadge} data-testid="sd-doc-state">{docState}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ---------------- BOZZA ---------------- */}
        {status === 'bozza' && (
          <div className="space-y-3" data-testid="sd-docs-bozza">
            <div className="flex flex-wrap gap-2">
              <Button onClick={generatePdf} disabled={generating} data-testid="sd-generate-pdf">
                {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                {pdfGenerated ? 'Rigenera PDF contratto' : 'Genera PDF contratto'}
              </Button>
              {pdfGenerated && (
                <Button variant="outline" onClick={generatePdf} disabled={generating} data-testid="sd-download-pdf">
                  <Download className="w-4 h-4 mr-1" /> Scarica PDF
                </Button>
              )}
            </div>
            {pdfGenerated ? (
              <div className="flex items-center justify-between gap-2 rounded-md border bg-slate-50 p-3 flex-wrap">
                <span className="text-sm text-slate-600">
                  PDF generato il <strong>{fmtDateTime(sub.pdf_generated_at)}</strong>. Il contratto è definitivo?
                </span>
                <Button size="sm" onClick={() => transition('pronto_per_firma')} disabled={transitioning} data-testid="sd-mark-ready">
                  {transitioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Segna pronto per firma
                </Button>
              </div>
            ) : (
              <p className="text-sm text-slate-500 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Genera il PDF del contratto prima di poterlo marcare "Pronto per firma".
              </p>
            )}
          </div>
        )}

        {/* ---------------- PRONTO PER FIRMA ---------------- */}
        {status === 'pronto_per_firma' && (
          <div className="space-y-4" data-testid="sd-docs-ready">
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={generatePdf} disabled={generating} data-testid="sd-download-pdf">
                {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Scarica PDF contratto
              </Button>
              <input
                ref={fileRef} type="file" accept="application/pdf" className="hidden"
                onChange={(e) => uploadSigned(e.target.files?.[0])} data-testid="sd-signed-input"
              />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="sd-upload-signed">
                {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {hasSigned ? 'Sostituisci contratto firmato' : 'Carica contratto firmato'}
              </Button>
            </div>

            {hasSigned ? (
              <div className="rounded-md border bg-emerald-50/60 p-3 space-y-2" data-testid="sd-signed-doc">
                <div className="text-sm">
                  <div><strong>Documento firmato:</strong> {signed.active.original_filename || signed.active.filename} <span className="text-slate-400">(v{signed.active.version})</span></div>
                  <div className="text-slate-600">Caricato il {fmtDateTime(signed.active.uploaded_at)} · Operatore: {operatorName(signed.active.uploaded_by)}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => downloadDoc(signed.active.id, signed.active.filename, true)} data-testid="sd-view-signed">
                    <Eye className="w-4 h-4 mr-1" /> Visualizza
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadDoc(signed.active.id, signed.active.filename, false)} data-testid="sd-download-signed">
                    <Download className="w-4 h-4 mr-1" /> Scarica
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Carica il PDF firmato per poter confermare la firma del contratto.
              </p>
            )}

            <div className="flex items-center justify-between gap-2 border-t pt-3 flex-wrap">
              <span className="text-sm text-slate-600">Confermando, lo stato passa a <strong>Firmato</strong> e il Quadro Economico viene sincronizzato.</span>
              <Button
                onClick={() => setConfirmSign(true)}
                disabled={!hasSigned || transitioning}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="sd-confirm-sign"
              >
                {transitioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Conferma contratto firmato
              </Button>
            </div>
          </div>
        )}

        {/* ---------------- FIRMATO / TERMINALI ---------------- */}
        {(status === 'firmato' || status === 'chiuso' || status === 'risolto') && (
          <div className="space-y-3" data-testid="sd-docs-signed">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Contratto firmato</span>
              {sub.signed_at && <span className="text-sm text-slate-500">il {fmtDateTime(sub.signed_at)}</span>}
            </div>
            {hasSigned ? (
              <div className="rounded-md border bg-white p-3 space-y-2">
                <div className="text-sm">
                  <div><strong>Documento firmato attivo:</strong> {signed.active.original_filename || signed.active.filename} <span className="text-slate-400">(v{signed.active.version})</span></div>
                  <div className="text-slate-600">Caricato il {fmtDateTime(signed.active.uploaded_at)} · Operatore: {operatorName(signed.active.uploaded_by)}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => downloadDoc(signed.active.id, signed.active.filename, true)} data-testid="sd-view-signed">
                    <Eye className="w-4 h-4 mr-1" /> Visualizza
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadDoc(signed.active.id, signed.active.filename, false)} data-testid="sd-download-signed">
                    <Download className="w-4 h-4 mr-1" /> Scarica
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">Nessun documento firmato disponibile.</p>
            )}
            <p className="text-xs text-slate-400">
              Le modifiche successive al contratto devono passare dal workflow "Modifiche Contrattuali".
            </p>
          </div>
        )}

        {status === 'annullato' && (
          <p className="text-sm text-slate-500" data-testid="sd-docs-cancelled">Contratto annullato: nessuna gestione documentale disponibile.</p>
        )}

        {signed.history?.length > 0 && (
          <div className="border-t pt-3">
            <p className="text-xs font-medium text-slate-500 mb-1">Versioni precedenti ({signed.history.length})</p>
            <div className="space-y-1">
              {signed.history.map((d) => (
                <div key={d.id} className="flex items-center justify-between text-xs text-slate-500">
                  <span>v{d.version} · {d.original_filename || d.filename} · {fmtDateTime(d.uploaded_at)}</span>
                  <button className="underline hover:text-slate-700" onClick={() => downloadDoc(d.id, d.filename, false)} data-testid={`sd-history-${d.id}`}>Scarica</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmSign} onOpenChange={setConfirmSign}>
        <AlertDialogContent data-testid="sd-confirm-sign-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermare la firma del contratto?</AlertDialogTitle>
            <AlertDialogDescription>
              Lo stato passerà a <strong>Firmato</strong>, verrà valorizzata la data di firma e il Quadro Economico
              verrà aggiornato con gli importi affidati. L'operazione è irreversibile: potrai chiudere o risolvere
              il contratto, ma non tornare in bozza.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sd-confirm-sign-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => transition('firmato')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sd-confirm-sign-ok">
              Firma e sincronizza Q.E.
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default SubcontractDocumentsCard;
